/**
 * Phase 3 Feature M — DDX-Plus session lifecycle (spec-aligned states + triggers).
 * Legacy Firestore values (`open`, `stable`, `closed`, `transcript_update`, …) normalize on read.
 */

import { isDdxStable } from './ddxDdxStability.js';

export const DDX_SESSION_STATES = Object.freeze({
  INITIAL: 'initial',
  REFINING: 'refining',
  NARROWING: 'narrowing',
  DISPOSITION: 'disposition',
  COMPLETED: 'completed',
});

export const DDX_SESSION_TRIGGERS = Object.freeze({
  NEW_TRANSCRIPT_CHUNK: 'new_transcript_chunk',
  MANUAL_REFRESH: 'manual_refresh',
  CLINICIAN_FINALIZED: 'clinician_finalized',
  EXAM_FINDINGS_ADDED: 'exam_findings_added',
  LAB_RESULTS_ADDED: 'lab_results_added',
  IMAGING_RESULTS_ADDED: 'imaging_results_added',
  CALCULATOR_COMPLETED: 'calculator_completed',
  CLINICIAN_RERANKED: 'clinician_reranked',
});

/** @type {Readonly<Record<string, string>>} */
const LEGACY_STATE_MAP = Object.freeze({
  open: DDX_SESSION_STATES.INITIAL,
  stable: DDX_SESSION_STATES.DISPOSITION,
  closed: DDX_SESSION_STATES.COMPLETED,
});

/** @type {Readonly<Record<string, string>>} */
const LEGACY_TRIGGER_MAP = Object.freeze({
  transcript_update: DDX_SESSION_TRIGGERS.NEW_TRANSCRIPT_CHUNK,
  session_complete: DDX_SESSION_TRIGGERS.CLINICIAN_FINALIZED,
});

/**
 * @param {string | null | undefined} s
 * @returns {string}
 */
export function normalizeLegacyDdxSessionState(s) {
  const v = String(s || '').trim().toLowerCase();
  if (!v) return DDX_SESSION_STATES.INITIAL;
  return LEGACY_STATE_MAP[v] || v;
}

/**
 * @param {string | null | undefined} t
 * @returns {string}
 */
export function normalizeDdxSessionTrigger(t) {
  const v = String(t || '').trim().toLowerCase();
  if (!v) return DDX_SESSION_TRIGGERS.NEW_TRANSCRIPT_CHUNK;
  return LEGACY_TRIGGER_MAP[v] || v;
}

/**
 * @param {string} trigger - normalized
 * @returns {boolean}
 */
function isInformationTrigger(trigger) {
  return (
    trigger === DDX_SESSION_TRIGGERS.NEW_TRANSCRIPT_CHUNK ||
    trigger === DDX_SESSION_TRIGGERS.MANUAL_REFRESH ||
    trigger === DDX_SESSION_TRIGGERS.EXAM_FINDINGS_ADDED ||
    trigger === DDX_SESSION_TRIGGERS.LAB_RESULTS_ADDED ||
    trigger === DDX_SESSION_TRIGGERS.IMAGING_RESULTS_ADDED ||
    trigger === DDX_SESSION_TRIGGERS.CALCULATOR_COMPLETED ||
    trigger === DDX_SESSION_TRIGGERS.CLINICIAN_RERANKED
  );
}

/**
 * @typedef {Object} TransitionMetrics
 * @property {number} candidateCount
 * @property {number} peakCandidateCount
 * @property {number} [narrowingRatio=0.3] drop from peak to count as narrowing
 * @property {boolean} [ddxStableHint] client/server precomputed `isDdxStable`
 * @property {import('./ddxTypes.js').DdxStabilitySession} [stabilitySession]
 * @property {import('./ddxTypes.js').ClinicalContextProfileId} [contextProfileId]
 */

/**
 * @param {string} currentState
 * @param {string} trigger
 * @param {TransitionMetrics} metrics
 * @returns {string}
 */
export function computeNextDdxSessionState(currentState, trigger, metrics) {
  const current = normalizeLegacyDdxSessionState(currentState);
  const trig = normalizeDdxSessionTrigger(trigger);

  if (trig === DDX_SESSION_TRIGGERS.CLINICIAN_FINALIZED) {
    return DDX_SESSION_STATES.COMPLETED;
  }
  if (current === DDX_SESSION_STATES.COMPLETED) {
    return DDX_SESSION_STATES.COMPLETED;
  }

  const peak = Math.max(1, Number(metrics.peakCandidateCount) || 1);
  const n = Number(metrics.candidateCount) || 0;
  const ratio = typeof metrics.narrowingRatio === 'number' ? metrics.narrowingRatio : 0.3;
  const dropped = n > 0 ? (peak - n) / peak : 0;

  let effective = current;
  if (effective === DDX_SESSION_STATES.INITIAL && isInformationTrigger(trig)) {
    effective = DDX_SESSION_STATES.REFINING;
  }

  if (n === 0) {
    if (effective === DDX_SESSION_STATES.INITIAL) return DDX_SESSION_STATES.INITIAL;
    return effective;
  }

  if (dropped > ratio) {
    return DDX_SESSION_STATES.NARROWING;
  }

  const stableByDdx =
    metrics.ddxStableHint === true ||
    (metrics.stabilitySession &&
      metrics.contextProfileId &&
      isDdxStable(metrics.stabilitySession, metrics.contextProfileId));

  const stableByMetrics = n > 0 && dropped <= ratio * 0.5;
  const canPlateauToDisposition =
    stableByDdx || (stableByMetrics && effective === DDX_SESSION_STATES.NARROWING);

  if (
    canPlateauToDisposition &&
    (effective === DDX_SESSION_STATES.NARROWING ||
      effective === DDX_SESSION_STATES.REFINING ||
      effective === DDX_SESSION_STATES.DISPOSITION)
  ) {
    return DDX_SESSION_STATES.DISPOSITION;
  }

  if (effective === DDX_SESSION_STATES.INITIAL) return DDX_SESSION_STATES.INITIAL;
  return effective;
}

/**
 * @param {number} peak
 * @param {number} next
 * @returns {number}
 */
export function updatePeakCandidateCount(peak, next) {
  const p = Number(peak) || 0;
  const n = Number(next) || 0;
  return Math.max(p, n);
}
